<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue';
import { refDebounced, useIntersectionObserver } from '@vueuse/core';
import { useQueryClient } from '@tanstack/vue-query';
import { useMultiFilterOptions } from '@/composables/api/useMultiFilterOptions';
import { useInspectorStore } from '@/stores/inspectorStore';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { MagnifyingGlassIcon } from '@heroicons/vue/24/solid';
import http from '@/services/http';
import type { MultiFilterOption } from '@/composables/api/useMultiFilterOptions';

const props = defineProps<{
    contactId: number;
    excludeOwners: Array<{ owner_type: string; owner_id: number }>;
}>();

const inspectorStore = useInspectorStore();
const queryClient = useQueryClient();
const searchQuery = ref('');
const debouncedSearchQuery = refDebounced(searchQuery, 300);
const isSubmitting = ref(false);

const selectedKeys = ref<Set<string>>(new Set());

const OWNER_TYPES = [
    { slug: 'account', ownerType: 'Account', label: 'Account' },
    { slug: 'location', ownerType: 'Location', label: 'Location' },
    { slug: 'location_zone', ownerType: 'LocationZone', label: 'Zone' },
] as const;

const selectedOwnerType = ref<string>(OWNER_TYPES[0].ownerType);

const selectedSlug = computed(() => {
    const match = OWNER_TYPES.find((ownerType) => ownerType.ownerType === selectedOwnerType.value);
    return match?.slug ?? '';
});

const { options, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, totalResults } = useMultiFilterOptions({
    slug: selectedSlug,
    ownerType: selectedOwnerType,
    searchQuery: debouncedSearchQuery,
});

const hasSearched = computed(() => debouncedSearchQuery.value.trim().length > 0);

const filteredOptions = computed(() => {
    if (!hasSearched.value || !options.value) return [];

    let result = [...options.value];

    if (props.excludeOwners.length > 0) {
        const excludeSet = new Set(
            props.excludeOwners.map((ownerExclude) => `${ownerExclude.owner_type}-${ownerExclude.owner_id}`)
        );
        result = result.filter(
            (opt) => !excludeSet.has(`${opt.owner_type}-${opt.id}`)
        );
    }
    return result;
});

// Infinite scroll sentinel
const scrollSentinel = useTemplateRef<HTMLDivElement>('scrollSentinel');

useIntersectionObserver(scrollSentinel, ([entry]) => {
    if (entry?.isIntersecting && hasNextPage.value && !isFetchingNextPage.value) {
        fetchNextPage();
    }
});

// Reset selections when switching owner type
watch(selectedOwnerType, () => {
    selectedKeys.value = new Set();
});

function optionKey(option: MultiFilterOption): string {
    return `${option.owner_type}-${option.id}`;
}

function optionLabel(option: MultiFilterOption): string {
    return option.display_string || option.name || String(option.id);
}

function toggleOption(option: MultiFilterOption) {
    const key = optionKey(option);
    const next = new Set(selectedKeys.value);
    if (next.has(key)) {
        next.delete(key);
    } else {
        next.add(key);
    }
    selectedKeys.value = next;
}

function isSelected(option: MultiFilterOption): boolean {
    return selectedKeys.value.has(optionKey(option));
}

const selectedCount = computed(() => selectedKeys.value.size);

async function addSelectedOwners() {
    if (isSubmitting.value || selectedKeys.value.size === 0) return;
    isSubmitting.value = true;
    try {
        const allOptions = options.value || [];
        const selectedOptions = allOptions.filter((opt) => selectedKeys.value.has(optionKey(opt)));
        await Promise.all(
            selectedOptions.map((option) =>
                http.post('/api/contactable', {
                    contact_id: props.contactId,
                    f_id: option.id,
                    f_type: option.owner_type,
                })
            )
        );
        await queryClient.invalidateQueries({ queryKey: ['search'] });
        inspectorStore.close();
    } finally {
        isSubmitting.value = false;
    }
}
</script>

<template>
    <div class="flex flex-col flex-1 min-h-0 overflow-hidden">
        <!-- Type selector + Search -->
        <div class="py-3 border-b px-4 shrink-0 space-y-2">
            <!-- Segmented type picker -->
            <div class="flex rounded-md border border-input overflow-hidden">
                <button
                    v-for="ownerType in OWNER_TYPES"
                    :key="ownerType.ownerType"
                    type="button"
                    class="flex-1 px-2 py-1.5 text-xs font-medium transition-colors text-center"
                    :class="selectedOwnerType === ownerType.ownerType
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'"
                    @click="selectedOwnerType = ownerType.ownerType"
                >
                    {{ ownerType.label }}
                </button>
            </div>
            <div class="relative">
                <MagnifyingGlassIcon class="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                    v-model="searchQuery"
                    placeholder="Search..."
                    class="pl-8"
                />
            </div>
        </div>

        <!-- Options list -->
        <div class="flex-1 min-h-0 overflow-y-auto px-4">
            <div v-if="hasSearched && isLoading" class="py-6 text-center text-sm text-muted-foreground">
                Loading...
            </div>
            <template v-else-if="filteredOptions.length > 0">
                <button
                    v-for="option in filteredOptions"
                    :key="optionKey(option)"
                    type="button"
                    class="w-full py-2 text-sm text-left flex items-center gap-3 hover:bg-muted rounded-md px-1 -mx-1"
                    @click="toggleOption(option)"
                >
                    <Checkbox
                        :model-value="isSelected(option)"
                        class="shrink-0 pointer-events-none"
                        tabindex="-1"
                    />
                    <span class="break-words">{{ optionLabel(option) }}</span>
                </button>
                <!-- Scroll sentinel for loading more -->
                <div ref="scrollSentinel" class="h-1" />
                <div v-if="isFetchingNextPage" class="py-3 text-center text-xs text-muted-foreground">
                    Loading more...
                </div>
            </template>
            <div v-else class="py-6 text-center text-sm text-muted-foreground">
                No results. Perform a search to find owners to add.
            </div>
        </div>

        <!-- Footer with Add button -->
        <div class="shrink-0 border-t p-4 space-y-2">
            <p v-if="filteredOptions.length > 0" class="text-xs text-muted-foreground text-center">
                Showing {{ filteredOptions.length }} of {{ totalResults - (options.length - filteredOptions.length) }} results
            </p>
            <Button
                class="w-full"
                :disabled="selectedCount === 0 || isSubmitting"
                @click="addSelectedOwners"
            >
                {{ isSubmitting ? 'Adding...' : `Add Owner${selectedCount !== 1 ? 's' : ''}` }}
                <span v-if="selectedCount > 0" class="ml-1">({{ selectedCount }})</span>
            </Button>
        </div>
    </div>
</template>
